import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { tokenStore } from '../lib/api';

const AuthContext = createContext(null);

// Inactivity timeout (auth cookies live on server, but we still log out idle users)
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const ROLES = {
  SUPER_ADMIN: 'SuperAdmin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  EMPLOYEE: 'Employé',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Logout — clears httpOnly cookies on the server + local state + token store
  const logout = useCallback(async (reason = 'manual') => {
    try {
      const refreshToken = tokenStore.getRefresh();
      await api.post('/auth/logout', refreshToken ? { refreshToken } : {});
    } catch (_) {
      // best-effort — even if the server call fails, clear local state
    }
    tokenStore.clear();
    localStorage.removeItem('lastActivity');
    setUser(null);

    // Notify other tabs
    localStorage.setItem('logout_event', Date.now().toString());
    localStorage.removeItem('logout_event');

    if (reason === 'inactivity') {
      console.log('Session expirée pour inactivité');
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    localStorage.setItem('lastActivity', Date.now().toString());

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (user) {
      inactivityTimerRef.current = setTimeout(() => {
        logout('inactivity');
      }, INACTIVITY_TIMEOUT);
    }
  }, [user, logout]);

  const checkSessionExpiry = useCallback(() => {
    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity) {
      const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
      if (timeSinceActivity > INACTIVITY_TIMEOUT) {
        logout('inactivity');
        return true;
      }
    }
    return false;
  }, [logout]);

  // Resolve the current user from the httpOnly cookie (no localStorage token).
  // The api interceptor will try a silent /auth/refresh on 401 automatically.
  const fetchUser = useCallback(async () => {
    try {
      if (checkSessionExpiry()) {
        setLoading(false);
        return;
      }
      const response = await api.get('/auth/me');
      const userData = response.data;
      if (userData.role === 'Viewer') {
        userData.role = 'Employé';
      }
      setUser(userData);
      resetInactivityTimer();
    } catch (error) {
      // No valid session — stay logged out (interceptor already tried refresh)
      localStorage.removeItem('lastActivity');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [checkSessionExpiry, resetInactivityTimer]);

  // Initial bootstrap — always try to resolve session via cookie (run once)
  const didBootstrap = useRef(false);
  useEffect(() => {
    if (didBootstrap.current) return;
    didBootstrap.current = true;
    fetchUser();
  }, [fetchUser]);

  // Cross-tab synchronization (no token in localStorage anymore)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'logout_event') {
        setUser(null);
      }
      if (e.key === 'lastActivity' && e.newValue) {
        lastActivityRef.current = parseInt(e.newValue, 10);
        resetInactivityTimer();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [resetInactivityTimer]);

  // Inactivity timer
  useEffect(() => {
    if (!user) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      return;
    }

    resetInactivityTimer();

    const handleActivity = () => resetInactivityTimer();
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    const checkInterval = setInterval(() => {
      checkSessionExpiry();
    }, 60000);

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(checkInterval);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [user, resetInactivityTimer, checkSessionExpiry]);

  // Re-check session when the tab regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!user && !loading) {
          fetchUser();
        } else if (user) {
          checkSessionExpiry();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, loading, checkSessionExpiry, fetchUser]);

  const login = async (identifier, password, turnstileToken) => {
    // Server sets httpOnly access + refresh cookies on success AND returns
    // tokens in the response body. We store both in sessionStorage so the
    // Bearer fallback works when cookies are stripped by a proxy/CDN.
    const response = await api.post('/auth/login', { identifier, password, turnstileToken });
    const { user: userData, accessToken, refreshToken } = response.data;
    if (accessToken) tokenStore.set(accessToken, refreshToken);
    localStorage.setItem('lastActivity', Date.now().toString());

    if (userData.role === 'Viewer') {
      userData.role = 'Employé';
    }
    setUser(userData);
    resetInactivityTimer();
    return userData;
  };

  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;
  const isAdmin = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(user?.role);
  const isManager = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER].includes(user?.role);
  const isEmployee = user?.role === ROLES.EMPLOYEE;

  const hasPermission = (permission) => {
    if (!user) return false;
    if (isSuperAdmin) return true;
    return user.permissions?.includes(permission) || false;
  };

  const hasAnyPermission = (permissions) => {
    if (!user) return false;
    if (isSuperAdmin) return true;
    return permissions.some(p => user.permissions?.includes(p));
  };

  const canAccessModule = (moduleName) => {
    if (!user) return false;
    if (isSuperAdmin) return true;
    return user.modules?.[moduleName] === true;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,

    isSuperAdmin,
    isAdmin,
    isManager,
    isEmployee,

    hasPermission,
    hasAnyPermission,
    canAccessModule,
    permissions: user?.permissions || [],
    modules: user?.modules || {},

    hasCompanyContext: !!user?.companyId,
    companyId: user?.companyId,

    resetInactivityTimer,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
